import React, { useContext, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import * as moment from "moment";
import "moment/locale/de";
import "moment/locale/en-gb";
import "moment/locale/fr";
import "moment/locale/it";
import _ from "lodash";
import LanguageContext from "../../helper/LanguageContext";
import MaterialIcon from "../MaterialIcon";
import EmailEmptyState from "../EmailEmptyState";
import TrialWarningBanner from "../TrialWarningBanner";
import EmailFeedRow from "../EmailFeedRow";
import ModernToggleSwitch from "../ModernToggleSwitch";
import InfoTip from "../InfoTip";
import { resolveStripeFlags } from "../../utils/stripeFeature";
import { getLocaleForLanguage } from "../../utils/composerUtils";
import "./ProjectEmailsSection.css";
import {
  OPEN_PROJECTS,
  RIGHT_TOP,
  SET_OPEN_SIDE_BAR,
  TOGGLE_PROJECT,
} from "../../store";

/**
 * ProjectEmailsSection (list)
 *
 * The room's "E-Mail" tab list, rendered INSIDE the rooms sidebar directly
 * below the Chat/E-Mail switch (i.e. where the chat groups normally show).
 * Each row is a compact email preview (sender / subject / snippet / date) like
 * the inbox. Clicking a row calls `onOpenEmail(item)` — the parent then opens
 * the email in the right pane (same behaviour as opening a chat group).
 *
 * Inbox / Sent / Drafts sub-tabs filter via the parent's `direction` prop
 * ("incoming" | "outgoing" | "draft").
 *
 * This component is purely presentational: the list data, the currently
 * selected email and the reader in the right pane are all owned by the parent
 * (ProjectDetail), so the reader can live in the conversation panel next to the
 * chat view instead of inside this sidebar.
 */
export default function ProjectEmailsSection({
  items = [],
  loading = false,
  selectedLinkId = null,
  isInternal: _isInternal = false,
  onOpenEmail,
  onRemoveEmail,
  direction = "incoming",
  onDirectionChange,
  emptyLabel,
  asDocuments: _asDocuments = false,
  onBookmarkEmail,
  isEmailBookmarked,
  deleting = false,
  roomBriefingEnabled = false,
  onRoomBriefingChange,
  roomBriefingSaving = false,
}) {
  const { strings, language } = useContext(LanguageContext);
  const history = useHistory();
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.profile);
  const subscriptionState = useSelector((state) => state.subscription) || {};
  const { stripeUiEnabled } = resolveStripeFlags(profile, subscriptionState);
  const hasActiveKjuupIoPlan = !stripeUiEnabled || !!subscriptionState.active;
  const hasUsedTrial = useSelector((state) => !!state.subscription?.hasUsedTrial);
  const isSent = direction === "outgoing";
  const isDraft = direction === "draft";

  // Leave rooms shell so /profile-setting|/subscription is not hidden
  // (App hides main routes while activeSideBar === 'project1').
  const leaveRoomsAndNavigate = (path, state) => {
    dispatch({ type: TOGGLE_PROJECT, payload: false });
    dispatch({ type: OPEN_PROJECTS, openProjects: [] });
    dispatch({ type: RIGHT_TOP, rightTop: "" });
    dispatch({ type: SET_OPEN_SIDE_BAR, payload: "profile" });
    history.push(path, state);
  };

  const sections = useMemo(() => {
    const locale = getLocaleForLanguage(language);
    const grouping = _.groupBy(items, (el) =>
      moment(el.receivedAt).locale(locale).format("MMMM, Y")
    );
    return _.map(grouping, (rows, label) => ({ label, rows }))
      .sort((a, b) => new Date(b.rows[0].receivedAt) - new Date(a.rows[0].receivedAt));
  }, [items, language]);

  const tabClass = (active) => `kj-tab${active ? " is-active" : ""}`;

  if (!hasActiveKjuupIoPlan) {
    return (
      <>
        <TrialWarningBanner />
        <EmailEmptyState
          variant={hasUsedTrial ? "trialEnded" : "noPlan"}
          onPrimary={() => leaveRoomsAndNavigate("/subscription")}
        />
      </>
    );
  }

  return (
    <div className="project-emails-list-sidebar">
      <TrialWarningBanner />
      {typeof onDirectionChange === "function" && (
        <div className="project-emails-section__tabs">
          <button
            type="button"
            className={tabClass(!isSent && !isDraft)}
            onClick={() => onDirectionChange("incoming")}
          >
            {strings.email_room_inbox || strings.email_folder_inbox || "Inbox"}
          </button>
          <button
            type="button"
            className={tabClass(isSent)}
            onClick={() => onDirectionChange("outgoing")}
          >
            {strings.email_room_sent || strings.email_folder_sent || "Sent"}
          </button>
          <button
            type="button"
            className={tabClass(isDraft)}
            onClick={() => onDirectionChange("draft")}
          >
            {strings.email_room_drafts || strings.email_folder_drafts || "Drafts"}
          </button>
        </div>
      )}

      {typeof onRoomBriefingChange === "function" && (
        <div className="room-briefing-toggle-block">
          <div className="room-briefing-toggle-row">
            <div className="room-briefing-toggle-label">
              <span>
                {strings.room_daily_briefing_label || strings.daily_briefing_label || "Individual project report"}
              </span>
              <InfoTip
                title={strings.room_daily_briefing_label || strings.daily_briefing_label || "Individual project report"}
                text={strings.room_daily_briefing_info || strings.daily_briefing_info}
              />
            </div>
            <ModernToggleSwitch
              id="room-daily-briefing-switch"
              checked={!!roomBriefingEnabled}
              disabled={!!roomBriefingSaving}
              onChange={(e) => onRoomBriefingChange(e.target.checked)}
              variant={_isInternal ? "green" : "orange"}
            />
          </div>
        </div>
      )}

      {loading ? (
        <div className="email-attachments-empty">{strings.email_loading || "Loading..."}</div>
      ) : !items.length ? (
        <div className="email-attachments-empty">
          <MaterialIcon icon="attach_email" size={40} color="#bbb" />
          <div className="project-emails-section__empty-text">
            {emptyLabel
              || (isDraft
                ? (strings.email_room_drafts_empty || "No drafts in this project")
                : isSent
                ? (strings.email_room_sent_empty || "No sent emails in this project")
                : (strings.email_room_empty || "No emails associated added to this project"))}
          </div>
        </div>
      ) : (
        sections.map((section) => (
          <div key={section.label}>
            <div className="email-attachment-date-header">
              <span className="email-attachment-date-label">
                <MaterialIcon icon="calendar_month" size={14} color="var(--kjuup-accent)" />
                {section.label}
              </span>
            </div>
            {section.rows.map((item) => (
              <EmailFeedRow
                key={item.id || item.messageId}
                item={item}
                selected={String(selectedLinkId) === String(item.id)}
                strings={strings}
                onOpen={onOpenEmail}
                onDelete={onRemoveEmail}
                deleting={deleting}
                onBookmark={onBookmarkEmail}
                isBookmarked={typeof isEmailBookmarked === "function" ? isEmailBookmarked(item) : false}
              />
            ))}
          </div>
        ))
      )}
    </div>
  );
}
